import { useQuery } from 'react-query';
import { chamarOktonBank } from '../oktonbank';

// Contrato do Backend: valor monetário chega como string já nas casas da
// Empresa, contagem como número. A conversão para somar ou formatar fica na
// tela.

// Todos os campos são opcionais do lado do back: vazio é "sem filtro".
export interface FiltrosDashboard {
  conta: string;
  dataini: string;
  datafim: string;
  moeda: string;
  centrocusto: string;
  departamento: string;
}

export interface DadosDashboard {
  saldototal: string;
  saldomesanterior: string;
  // Percentual com duas casas; null quando o mês anterior fechou zerado.
  variacaosaldo: string | null;
  movimentoperiodo: string;
  contas: number;
  extratototal: number;
  extratopendente: number;
  valorpendente: string;
  extratoconciliado: number;
  divergencias: number;
  valordivergente: string;
  // Vínculos gravados, não linhas do extrato.
  conciliados: number;
  titulopendente: number;
  valorconciliado: string;
}

export type Agrupamento = 'dia' | 'semana' | 'mes';

export interface PontoEvolucao {
  // yyyy-MM-dd: o dia, a segunda-feira da semana ou o primeiro dia do mês.
  periodo: string;
  conciliados: number;
  pendentes: number;
  divergentes: number;
  entradas: string;
  saidas: string;
  saldo: string;
}

// Um lançamento do extrato, para o gráfico de um dia só. entrada e saida
// chegam positivos; o que não se aplica vem zerado.
export interface LancamentoDoDia {
  id_exb: string;
  descricao: string;
  conta: string | null;
  entrada: string;
  saida: string;
  conciliado: string;
}

export interface DadosEvolucao {
  agrupamento: Agrupamento;
  saldoinicial: string;
  rows: PontoEvolucao[];
  // Só com um dia no filtro: o extrato não guarda hora, então o gráfico de
  // Entradas x Saídas mostra cada lançamento daquele dia.
  lancamentos: LancamentoDoDia[] | null;
  // Também só com um dia: o dia hora a hora, pela hora do lançamento
  // (data_exb). O que não tem hora está em 00:00:00 e cai em 00h.
  horas: PontoHora[] | null;
  // Algum lançamento do dia tem hora diferente de 00:00. Sem nenhum, os
  // gráficos voltam à visão sem hora.
  temhora: boolean;
}

export interface PontoHora {
  // '00h' a '23h'
  hora: string;
  conciliados: number;
  pendentes: number;
  divergentes: number;
  entradas: string;
  saidas: string;
  // Saldo ao fim da hora.
  saldo: string;
}

export interface ContaDashboard {
  id_cts: string;
  descricao_cts: string;
  numero_cts: string | null;
  nome_ban: string | null;
  confcomp_ban: string | null;
  saldo: string;
  total: number;
  pendentes: number;
  valorpendente: string;
  conciliados: number;
  divergencias: number;
  valordivergente: string;
  percentual: number | null;
  // yyyy-MM-dd HH:mm:ss da última importação de extrato.
  ultimaatualizacao: string | null;
  sematualizacao: boolean;
}

export interface ResumoPendencia {
  quantidade: number;
  valor: string;
  // A Conta com mais casos, para o link já abrir nela.
  conta: string | null;
}

export interface DadosPendencias {
  semcorrespondencia: ResumoPendencia;
  possiveiscorrespondencias: ResumoPendencia;
  divergencias: ResumoPendencia;
  contassematualizacao: {
    quantidade: number;
    contas: {
      id_cts: string;
      descricao_cts: string;
      ultimaatualizacao: string | null;
    }[];
  };
}

export interface Movimentacao {
  categoria: string;
  quantidade: number;
  valor: string;
}

export interface UltimaConciliacao {
  id_cbc: string;
  cadastro_cbc: string;
  data_exb: string;
  conta_exb: string;
  descricao_cts: string | null;
  descricao_exb: string;
  valor_cbc: string;
  valor_exb: string;
  tipo_cbc: string;
  origem_cbc: string;
  usuario: string | null;
  divergente: boolean;
}

// O Dashboard é leitura: meio minuto sem refazer a consulta evita uma ida ao
// servidor a cada troca de aba sem deixar o número velho.
const STALE = 1000 * 30;

// keepPreviousData: ao trocar o filtro, o gráfico continua com os dados
// anteriores enquanto os novos chegam e anima de um para o outro, em vez de
// sumir numa tela de carregamento e voltar com a animação inicial.
const OPCOES = { staleTime: STALE, keepPreviousData: true };

function consulta<T>(action: string, filtros: FiltrosDashboard) {
  return chamarOktonBank<T>('OBDashboard', action, { ...filtros });
}

function chave(action: string, filtros: FiltrosDashboard) {
  return ['dashboard', action, filtros];
}

export function useDashboard(filtros: FiltrosDashboard) {
  return useQuery(
    chave('listar', filtros),
    () => consulta<DadosDashboard>('listar', filtros),
    OPCOES,
  );
}

export function useDashboardEvolucao(
  filtros: FiltrosDashboard,
  options?: { enabled?: boolean },
) {
  return useQuery(
    chave('evolucao', filtros),
    () => consulta<DadosEvolucao>('evolucao', filtros),
    { ...OPCOES, ...options },
  );
}

export function useDashboardContas(filtros: FiltrosDashboard) {
  return useQuery(
    chave('contas', filtros),
    async () => {
      const dados = await consulta<{ rows: ContaDashboard[] }>(
        'contas',
        filtros,
      );
      return Array.isArray(dados?.rows) ? dados.rows : [];
    },
    OPCOES,
  );
}

export function useDashboardPendencias(filtros: FiltrosDashboard) {
  return useQuery(
    chave('pendencias', filtros),
    () => consulta<DadosPendencias>('pendencias', filtros),
    OPCOES,
  );
}

export function useDashboardMovimentacoes(filtros: FiltrosDashboard) {
  return useQuery(
    chave('movimentacoes', filtros),
    async () => {
      const dados = await consulta<{ rows: Movimentacao[] }>(
        'movimentacoes',
        filtros,
      );
      return Array.isArray(dados?.rows) ? dados.rows : [];
    },
    OPCOES,
  );
}

export function useDashboardUltimasConciliacoes(filtros: FiltrosDashboard) {
  return useQuery(
    chave('ultimasConciliacoes', filtros),
    async () => {
      const dados = await consulta<{ rows: UltimaConciliacao[] }>(
        'ultimasConciliacoes',
        filtros,
      );
      return Array.isArray(dados?.rows) ? dados.rows : [];
    },
    OPCOES,
  );
}
